"use client";

import type { ReactNode } from "react";
import { useTranslations } from "next-intl";
import { Mic, MicOff } from "lucide-react";
import { Textarea } from "@/shared/ui/inputs/textarea";
import {
  HOUR_SLOT_COUNT,
  LFG_FORMATS,
  LFG_LANGUAGES,
  LFG_MIC,
  LFG_RANKS,
  LFG_ROLES,
  LFG_SERVERS,
  toggleIn,
  toggleRank,
} from "../model/board-filters";
import { DESCRIPTION_MAX, togglePlayerRole, toggleTeamRole, type PostForm } from "../model/post-form";
import { BoardChip } from "./board-chip";
import { LfgRoleIcon, RankEmblem } from "./board-card-parts";

interface FieldsProps {
  form: PostForm;
  onChange: (next: PostForm) => void;
  tzLabel: string;
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-[11px] font-bold uppercase tracking-[1.2px] text-muted-foreground">{title}</h3>
      {hint && <p className="-mt-1 mb-2 text-xs text-muted-foreground">{hint}</p>}
      {children}
    </section>
  );
}

const range = (n: number) => [...Array(n).keys()];

export function PostFormFields({ form, onChange, tzLabel }: FieldsProps) {
  const t = useTranslations("LfgBoard");
  const isPlayer = form.type === "Lfg";
  const set = (patch: Partial<PostForm>) => onChange({ ...form, ...patch });

  return (
    <div className="flex flex-col gap-4">
      <Section title={isPlayer ? t("form.rolesPlayer") : t("form.rolesTeam")}>
        <div className="grid grid-cols-3 gap-1.5">
          {LFG_ROLES.map((r) => {
            const order = isPlayer ? (form.roles[0] === r ? 1 : form.secondary === r ? 2 : 0) : 0;
            const pressed = isPlayer ? order > 0 : form.roles.includes(r);
            return (
              <BoardChip
                key={r}
                pressed={pressed}
                title={order === 1 ? t("form.primaryRole") : order === 2 ? t("form.secondaryRole") : undefined}
                onPressedChange={() => onChange(isPlayer ? togglePlayerRole(form, r) : toggleTeamRole(form, r))}
                icon={<LfgRoleIcon role={r} size={15} />}
              >
                {t(`role.${r}`)}
                {order > 0 && <sup className="ml-0.5 text-[9px]">{order}</sup>}
              </BoardChip>
            );
          })}
        </div>
      </Section>

      {!isPlayer && (
        <Section title={t("form.rank")} hint={t("form.rankHint")}>
          <div className="grid grid-cols-4 gap-1.5">
            {LFG_RANKS.map((r, tier) => (
              <BoardChip
                key={r}
                pressed={!!form.rank && tier >= form.rank.min && tier <= form.rank.max}
                onPressedChange={() => set({ rank: toggleRank(form.rank, tier) })}
                icon={<RankEmblem rank={r} size={15} />}
              >
                {t(`rank.${r}`)}
              </BoardChip>
            ))}
          </div>
        </Section>
      )}

      <Section title={t("groupMic")}>
        <div className="grid grid-cols-3 gap-1.5">
          {LFG_MIC.map((m) => (
            <BoardChip
              key={m}
              pressed={form.mic === m}
              onPressedChange={() => set({ mic: m })}
              icon={
                m === "with" ? <Mic aria-hidden className="size-3.5 text-score-win" /> :
                m === "without" ? <MicOff aria-hidden className="size-3.5 text-destructive" /> : undefined
              }
            >
              {t(`mic.${m}`)}
            </BoardChip>
          ))}
        </div>
      </Section>

      <Section title={t("groupHours", { tz: tzLabel })}>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(78px,1fr))] gap-1.5">
          {range(HOUR_SLOT_COUNT).map((slot) => (
            <BoardChip
              key={slot}
              pressed={form.hourSlots.includes(slot)}
              onPressedChange={() => set({ hourSlots: toggleIn(form.hourSlots, slot, range(HOUR_SLOT_COUNT)) })}
            >
              {t("hoursRange", { from: String(slot * 3).padStart(2, "0"), to: String(slot * 3 + 3).padStart(2, "0") })}
            </BoardChip>
          ))}
        </div>
      </Section>

      <Section title={t("groupServer")}>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(64px,1fr))] gap-1.5">
          {LFG_SERVERS.map((s) => (
            <BoardChip key={s} pressed={form.servers.includes(s)} onPressedChange={() => set({ servers: toggleIn(form.servers, s, LFG_SERVERS) })}>
              {s}
            </BoardChip>
          ))}
        </div>
      </Section>

      <Section title={t("groupFormat")}>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(96px,1fr))] gap-1.5">
          {LFG_FORMATS.map((f) => (
            <BoardChip key={f} pressed={form.formats.includes(f)} onPressedChange={() => set({ formats: toggleIn(form.formats, f, LFG_FORMATS) })}>
              {t(`format.${f}`)}
            </BoardChip>
          ))}
        </div>
      </Section>

      <Section title={t("groupLanguage")}>
        <div className="grid grid-cols-2 gap-1.5">
          {LFG_LANGUAGES.map((l) => (
            <BoardChip key={l} pressed={form.languages.includes(l)} onPressedChange={() => set({ languages: toggleIn(form.languages, l, LFG_LANGUAGES) })}>
              {t(`language.${l}`)}
            </BoardChip>
          ))}
        </div>
      </Section>

      <Section title={t("form.description")}>
        <Textarea
          rows={3}
          value={form.description}
          maxLength={DESCRIPTION_MAX}
          onChange={(e) => set({ description: e.target.value })}
          placeholder={t("form.descriptionPlaceholder")}
          aria-label={t("form.description")}
          className="min-h-[76px] resize-y text-[13px] font-semibold"
        />
        <p className="mt-1 text-right text-[11px] text-muted-foreground">{form.description.length} / {DESCRIPTION_MAX}</p>
      </Section>
    </div>
  );
}
